/**
 * Fiat Donation Service
 * Handles NGN fiat donations via Flutterwave v4 Orchestrator
 */

import axios from 'axios';
import { API_CONFIG, FIAT_CURRENCY, FIAT_DONATION_LIMITS, FLUTTERWAVE_FEE_INFO } from '../constants/index.js';

class FiatDonationService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.fiatPaymentsEndpoint = `${API_CONFIG.ENDPOINTS.PAYMENTS}/fiat`;
  }

  /**
   * Initialize a fiat donation payment
   * @param {string} projectId - Project ID
   * @param {number} amount - Donation amount in NGN
   * @param {string} email - Donor email
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} API response
   */
  async initializePayment(projectId, amount, email, metadata = {}) {
    try {
      const response = await axios.post(`${this.baseURL}${this.fiatPaymentsEndpoint}/initialize`, {
        projectId,
        amount: parseFloat(amount),
        email,
        metadata
      });

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Verify payment status
   * @param {string} reference - Payment reference
   * @returns {Promise<Object>} API response
   */
  async verifyPayment(reference) {
    try {
      const response = await axios.get(`${this.baseURL}${this.fiatPaymentsEndpoint}/verify/${reference}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation by reference
   * @param {string} reference - Payment reference
   * @returns {Promise<Object>} API response
   */
  async getDonationByReference(reference) {
    try {
      const response = await axios.get(`${this.baseURL}${this.fiatPaymentsEndpoint}/${reference}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Calculate Flutterwave fee (for display purposes)
   * @param {number} amount - Amount in NGN
   * @returns {number} Estimated Flutterwave fee
   */
  calculateFlutterwaveFee(amount) {
    const numAmount = parseFloat(amount);
    const fee = numAmount * (FLUTTERWAVE_FEE_INFO.percentage / 100);

    return parseFloat(fee.toFixed(2));
  }

  /**
   * Calculate commission breakdown for display
   * @param {number} amount - Donation amount in NGN
   * @returns {Object} Commission breakdown
   */
  calculateCommission(amount) {
    const grossAmount = parseFloat(amount);
    const paystackFee = this.calculateFlutterwaveFee(grossAmount);
    const platformFee = grossAmount * 0.0199; // 1.99%
    const netAmount = grossAmount - paystackFee - platformFee;

    return {
      gross: parseFloat(grossAmount.toFixed(2)),
      paystackFee: parseFloat(paystackFee.toFixed(2)), // keeping var name for DB compatibility if needed
      flutterwaveFee: parseFloat(paystackFee.toFixed(2)), // UI friendly name
      platformFee: parseFloat(platformFee.toFixed(2)),
      net: parseFloat(netAmount.toFixed(2)),
      totalFees: parseFloat((paystackFee + platformFee).toFixed(2)),
      currency: FIAT_CURRENCY.code
    };
  }

  /**
   * Validate donation amount
   * @param {number} amount - Amount to validate
   * @returns {Object} Validation result
   */
  validateDonationAmount(amount) {
    const numAmount = parseFloat(amount);

    if (isNaN(numAmount)) {
      return { valid: false, error: 'Invalid amount format' };
    }

    if (numAmount < FIAT_DONATION_LIMITS.MIN_AMOUNT) {
      return {
        valid: false,
        error: `Minimum donation amount is ${FIAT_CURRENCY.symbol}${FIAT_DONATION_LIMITS.MIN_AMOUNT.toLocaleString()}`
      };
    }

    if (numAmount > FIAT_DONATION_LIMITS.MAX_AMOUNT) {
      return {
        valid: false,
        error: `Maximum donation amount is ${FIAT_CURRENCY.symbol}${FIAT_DONATION_LIMITS.MAX_AMOUNT.toLocaleString()}`
      };
    }

    return { valid: true };
  }

  /**
   * Get Flutterwave configuration for the frontend SDK
   * @param {Object} paymentData - Data needed for checkout
   * @returns {Object} Configuration object for useFlutterwave
   */
  getFlutterwaveConfig(paymentData) {
    const { amount, email, name, reference, projectId, donationId, publicKey, paymentMethod } = paymentData;

    return {
      public_key: publicKey || import.meta.env.VITE_FLW_PUBLIC_KEY || '',
      tx_ref: reference,
      amount: parseFloat(amount),
      currency: FIAT_CURRENCY.code,
      payment_options: paymentMethod || 'card,ussd,opay,account',
      customer: {
        email: email,
        name: name,
      },
      meta: {
        project_id: projectId,
        donation_id: donationId
      },
      customizations: {
        title: 'Crowblock Donation',
        description: 'Payment for project support',
        logo: `${import.meta.env.VITE_SITE_URL}/images/CrowBlock%20Logo.png`,
      },
    };
  }

  /**
   * Handle Flutterwave v3 payment closure/success (client-side)
   * This is usually called from the onSuccess/onClose callbacks of the hooks
   */
  handlePaymentResponse(response) {
    // response will contain the transaction details from Flutterwave
    return response;
  }

  /**
   * Format NGN amount for display
   * @param {number} amount - Amount to format
   * @returns {string} Formatted amount
   */
  formatAmount(amount) {
    return `${FIAT_CURRENCY.symbol}${parseFloat(amount).toLocaleString()}`;
  }

  /**
   * Format time ago for donations
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted time ago
   */
  formatTimeAgo(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return date.toLocaleDateString();
  }

  /**
   * Handle API errors consistently
   * @param {Object} error - Axios error object
   * @returns {Object} Formatted error
   */
  handleError(error) {
    if (error.response) {
      const { data, status } = error.response;
      return {
        message: data?.error?.message || 'An error occurred',
        code: data?.error?.code || 'UNKNOWN_ERROR',
        status,
        details: data?.error?.details
      };
    } else if (error.request) {
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        status: 0
      };
    } else {
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        status: 0
      };
    }
  }
}

// Create and export singleton instance
const fiatDonationService = new FiatDonationService();
export default fiatDonationService;